import logger from '../Helpers/logger';
const DB_NAME = 'chatsdb';
const DB_VERSION = 1;

// Centralized schema definition
const SCHEMA = {
    version: DB_VERSION,
    stores: {
        groups: {
            keyPath: null,
            indexes: [],
        },
        recent_chats: {
            keyPath: null,
            indexes: [{ name: 'timestamp', keyPath: ['timestamp', 'messageTime'], unique: false }],
        },
        messages: {
            keyPath: 'msgId',
            indexes: [{ name: 'userJid_timestamp', keyPath: ['userJid', 'timestamp'], unique: false }],
        },
    },
};

/**
 * Centralized Database Connection Manager
 * Ensures only ONE database connection is opened and shared across all helpers
 */
class DbConnectionManager {
    constructor() {
        if (DbConnectionManager.instance) {
            return DbConnectionManager.instance;
        }

        this.dbPromise = null;
        this.isInitializing = false;
        DbConnectionManager.instance = this;
    }

    /**
     * Opens the database with centralized schema management
     */
    openDB(version = SCHEMA.version) {
        return new Promise((resolve, reject) => {
            logger.debug(`[DbConnectionManager] Opening IndexedDB: ${DB_NAME}, Version: ${version}`);
            const request = indexedDB.open(DB_NAME, version);

            request.onerror = event => {
                logger.error('[DbConnectionManager] Open Error:', event.target.error);
                reject(event.target.error);
            };

            request.onsuccess = event => {
                const db = event.target.result;

                // Handle version change events (from other tabs upgrading the database)
                db.onversionchange = () => {
                    logger.warn('[DbConnectionManager] Database version change detected from another tab');
                    db.close();
                };

                // Handle unexpected closures
                db.onclose = () => {
                    logger.warn('[DbConnectionManager] Database connection closed unexpectedly');
                };

                logger.debug('[DbConnectionManager] Database opened successfully');
                resolve(db);
            };

            request.onupgradeneeded = event => {
                logger.debug(
                    `[DbConnectionManager] Upgrade needed. Old Version: ${event.oldVersion}, New Version: ${event.newVersion}`,
                );
                const db = event.target.result;

                // Create all stores defined in schema
                Object.entries(SCHEMA.stores).forEach(([storeName, config]) => {
                    let objectStore;

                    if (!db.objectStoreNames.contains(storeName)) {
                        logger.debug(`[DbConnectionManager] Creating object store: ${storeName}`);
                        const storeConfig = config.keyPath ? { keyPath: config.keyPath } : undefined;
                        objectStore = db.createObjectStore(storeName, storeConfig);
                    } else {
                        objectStore = event.target.transaction.objectStore(storeName);
                    }

                    // Create indexes
                    config.indexes.forEach(index => {
                        if (!objectStore.indexNames.contains(index.name)) {
                            logger.debug(`[DbConnectionManager] Creating index: ${index.name} on ${storeName}`);
                            objectStore.createIndex(index.name, index.keyPath, { unique: index.unique });
                        }
                    });
                });

                logger.debug('[DbConnectionManager] Schema upgrade complete');
            };

            request.onblocked = _event => {
                logger.warn(
                    '[DbConnectionManager] Database upgrade blocked. Please close other tabs with this site open.',
                );
            };
        });
    }

    /**
     * Get the database connection (creates if doesn't exist)
     * This is the main method that all helpers should use
     */
    async getConnection() {
        // If already initializing, wait for it
        if (this.isInitializing) {
            return this.dbPromise;
        }

        // If connection exists and is valid, return it
        if (this.dbPromise) {
            try {
                const db = await this.dbPromise;
                // Verify connection is still open
                if (db && !db.objectStoreNames) {
                    // Connection is closed, reinitialize
                    logger.debug('[DbConnectionManager] Connection closed, reinitializing...');
                    this.dbPromise = null;
                } else {
                    return db;
                }
            } catch (error) {
                logger.error('[DbConnectionManager] Error checking connection:', error);
                this.dbPromise = null;
            }
        }

        // Initialize new connection
        this.isInitializing = true;
        this.dbPromise = this.openDB().finally(() => {
            this.isInitializing = false;
        });

        return this.dbPromise;
    }

    /**
     * Close the database connection (useful for testing/cleanup)
     */
    async closeConnection() {
        if (this.dbPromise) {
            try {
                const db = await this.dbPromise;
                db.close();
                logger.debug('[DbConnectionManager] Connection closed');
            } catch (error) {
                logger.error('[DbConnectionManager] Error closing connection:', error);
            }
            this.dbPromise = null;
        }
    }

    /**
     * Delete the database (useful for testing/reset)
     */
    static deleteDatabase() {
        return new Promise((resolve, reject) => {
            logger.debug('[DbConnectionManager] Deleting database:', DB_NAME);
            const request = indexedDB.deleteDatabase(DB_NAME);

            request.onsuccess = () => {
                logger.debug('[DbConnectionManager] Database deleted successfully');
                resolve();
            };

            request.onerror = event => {
                logger.error('[DbConnectionManager] Error deleting database:', event.target.error);
                reject(event.target.error);
            };

            request.onblocked = () => {
                logger.warn('[DbConnectionManager] Database deletion blocked. Please close other tabs.');
            };
        });
    }
}

// Export singleton instance
const dbConnectionManager = new DbConnectionManager();
export default dbConnectionManager;
